import React, { useState, useEffect } from 'react';
import quizData from '../jsons/Multiple_choices.json';
import MathDisplay from './MathDisplay';
import TestQuestion from './TestQuestion';
import { usePremium } from '../contexts/PremiumContext';

/**
 * The IntelligentQuiz component generates a personalized quiz based on the user's past performance.
 * It identifies topics the user has struggled with and creates a weighted quiz to help them practice.
 * This is a premium feature that requires subscription access.
 * @param {object} props - The component's props.
 * @param {function} props.onExit - A function to call when the quiz is finished.
 * @param {function} props.onShowPayment - A function to call when premium upgrade is needed.
 */
const IntelligentQuiz = ({ onExit, onShowPayment }) => {
  const { isPremium: userHasPremium, getPremiumStatusInfo } = usePremium();
  const [intelligentQuestions, setIntelligentQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [maxAttempts] = useState(3);
  const [wrongAnswers, setWrongAnswers] = useState(new Set());

  /**
   * This effect runs when the component mounts. It generates the intelligent quiz questions.
   * It retrieves user performance from localStorage, identifies weak topics, and creates a weighted question list.
   */
  useEffect(() => {
    // Only generate questions if user has premium access
    if (!userHasPremium) return;
    
    const userPerformance = JSON.parse(localStorage.getItem('userPerformance')) || {};
    const allQuestions = Object.values(quizData).flat();
    
    const sortedTopics = Object.keys(userPerformance).sort((a, b) => userPerformance[b] - userPerformance[a]);

    let weightedQuestions = [];
    sortedTopics.forEach(topic => {
      const topicQuestions = allQuestions.filter(q => q.topic === topic);
      const numQuestionsToAdd = Math.min(topicQuestions.length, userPerformance[topic]);
      weightedQuestions.push(...topicQuestions.slice(0, numQuestionsToAdd));
    });

    const randomQuestions = allQuestions.filter(q => !weightedQuestions.includes(q));
    const shuffledRandom = randomQuestions.sort(() => 0.5 - Math.random());
    
    const finalQuiz = [...weightedQuestions, ...shuffledRandom.slice(0, 10 - weightedQuestions.length)];

    const shuffledQuiz = finalQuiz.sort(() => 0.5 - Math.random());
    setIntelligentQuestions(shuffledQuiz);
    setTotalQuestions(shuffledQuiz.length);
  }, [userHasPremium]);

  // Check if user has premium access
  if (!userHasPremium) {
    return (
      <div className="text-center py-16">
        <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-8 max-w-md mx-auto">
          <div className="text-6xl mb-4">🧠</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Premium Content</h3>
          <p className="text-gray-600 mb-4">
            Intelligent Quiz is a premium feature that analyzes your performance and creates personalized quizzes to help you improve.
          </p>
          <button
            onClick={() => onShowPayment && onShowPayment()}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-3 px-6 rounded-lg transition-all transform hover:scale-105"
          >
            🔓 Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = intelligentQuestions[currentQuestionIndex];

  /**
   * Submits the user's answer, checks if it's correct, and updates the score and attempts.
   * @param {string} answer - The selected answer from TestQuestion component.
   */
  const handleSubmitAnswer = (answer) => {
    if (!answer) return;

    const isCorrect = answer === currentQuestion.right_answer;
    setShowResult(true);
    
    if (isCorrect) {
      if (attempts === 0) {
        setScore(score + 1);
      }
    } else {
      setWrongAnswers(prev => new Set([...prev, answer]));
      setAttempts(attempts + 1);
    }
  };

  /**
   * Moves to the next question in the quiz or finishes the quiz if all questions have been answered.
   */
  const handleNextQuestion = () => {
    if (currentQuestionIndex < intelligentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowResult(false);
      setAttempts(0);
      setWrongAnswers(new Set());
    } else {
      alert(`Intelligent Quiz ပြီးဆုံးပါပြီ! သင်၏ ရမှတ်: ${score}/${totalQuestions}`);
      onExit();
    }
  };

  if (intelligentQuestions.length === 0) {
    return (
      <div className="text-center">
        <p>No questions available for the intelligent quiz yet. Please take some regular quizzes first.</p>
        <button onClick={onExit} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">Back to Chapters</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
        <div className="flex justify-end">
            <button onClick={onExit} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg">Exit Quiz</button>
        </div>
        <TestQuestion
            question={currentQuestion}
            questionNumber={currentQuestionIndex + 1}
            onAnswerSubmit={handleSubmitAnswer}
            isAnswered={showResult}
        />
        
        {/* Navigation */}
        {showResult && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center">
              <button
                onClick={handleNextQuestion}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                {currentQuestionIndex < intelligentQuestions.length - 1 ? 'နောက်မေးခွန်း' : 'ပြီးဆုံးမည်'}
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default IntelligentQuiz;